import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';

interface TrialInfo {
  end_date: string | null;
  days_remaining: number | null;
}

interface StripeInfo {
  name: string;
  email: string;
  subscription_status: string | null;
  contract_units: number | null;
  trial: TrialInfo | null;
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  address_country: string | null;
}

interface Customer {
  customer_tag: string;
  description: string;
  stripe: StripeInfo;
  stats: Record<string, never>;
  retention_period_days: number;
  retention_unit: string;
}

interface CustomerContextType {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  refreshCustomers: () => Promise<void>;
  isUpdating: boolean;
  setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export const CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/customers`);
      if (!response.ok) throw new Error('Failed to fetch customers');
      const data = await response.json();
      setCustomers(data.customers);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load customers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStripeData = async () => {
    // Prevent updates if one is in progress or if last update was less than 5 minutes ago
    const now = Date.now();
    if (isUpdating || (now - lastUpdateTime < 5 * 60 * 1000)) return;

    try {
      setIsUpdating(true);
      const updateResponse = await fetch(`${API_BASE_URL}/api/update_customers`);
      if (!updateResponse.ok) {
        console.warn('Failed to update customers from Stripe:', await updateResponse.text());
        return;
      }
      setLastUpdateTime(now);
    } catch (err) {
      console.error('Error updating Stripe data:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const refreshCustomers = async () => {
    // First update from Stripe
    await updateStripeData();
    // Then fetch the updated data
    await fetchCustomers();
  };

  useEffect(() => {
    // Only fetch customer data initially, don't update from Stripe
    fetchCustomers();
  }, []);

  return (
    <CustomerContext.Provider 
      value={{ 
        customers, 
        loading, 
        error, 
        refreshCustomers,
        isUpdating,
        setCustomers,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomers = () => {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomers must be used within a CustomerProvider');
  }
  return context;
};

export type { Customer, TrialInfo, StripeInfo }; 